/**
 * Safe wrappers around localStorage.
 *
 * localStorage can throw, not just return null: reading `window.localStorage`
 * itself throws SecurityError when storage is disabled (blocked site data /
 * cookies), and setItem throws QuotaExceededError when storage is full or in
 * some private-browsing contexts. An unguarded access on a hot path (e.g. the
 * request interceptor, which runs on every API call) would break that path.
 *
 * These helpers are drop-in and backwards compatible: they read/write the exact
 * same keys and raw string values as direct localStorage calls, so values
 * already stored by older builds keep working unchanged. They only differ by
 * swallowing the throw and returning null / no-op instead.
 */

export const safeGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

export const safeSetItem = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore: storage unavailable / quota exceeded
  }
};

export const safeRemoveItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore: storage unavailable
  }
};

/**
 * Reads a JSON-encoded value. Returns null if the key is absent, storage
 * throws, or the stored value is not valid JSON — never throws.
 *
 * Backwards compatible with `JSON.parse(localStorage.getItem(key))`: a valid
 * JSON value parses identically; an absent key yields null (matching
 * `JSON.parse(null)`); only the corrupt-value case differs, returning null
 * instead of throwing.
 */
export const safeGetJSON = <T>(key: string): T | null => {
  const raw = safeGetItem(key);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};
